import { DateTime } from "luxon";

import { buildSitewideAnalyticsPayload } from "@intelligems/ig-graphs/sitewide-analytics-filters";
import { AnalyzeSitewideFrontendTinybirdRequestBodyType } from "@intelligems/ig-types/schemas/analytics/analyze-sitewide";
import { DatasetId } from "@intelligems/ig-types/schemas/analytics/base";
import { getMaxZScore } from "@src/api/analytics/utils";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useSelector } from "@src/store";
import { selectedSitewideDashboardFilters } from "@src/store/slices/sitewideDashboardFilters";
import { SitewideOrdersScope } from "@src/store/types/sitewideDashboardFilters";

export const useBuildSitewideAnalyticsPayload = (): {
  build: (
    datasetId: DatasetId,
    parameters?: Partial<AnalyzeSitewideFrontendTinybirdRequestBodyType>,
    timezone?: string
  ) => AnalyzeSitewideFrontendTinybirdRequestBodyType;
} => {
  const maxZScore = useConfiguration<number>("max_z_score");
  const sitewideDashboardFilters = useSelector(
    selectedSitewideDashboardFilters
  );

  const {
    pageName,
    startTime,
    endTime,
    ordersMatchedToSession,
    snapshotsEndTime,
    snapshotsStartTime,
    snapshotsTimeFrame,
    snapshotsComparisonEndTime,
    snapshotsComparisonStartTime,
    snapshotsComparisonTimeFrame,
    audienceRatioToggle,
    isTimeRangeUpdated,
    conversionFunnel,
    ...restOfDashboardFilters
  } = sitewideDashboardFilters;

  return {
    build: (datasetId, parameters, timezone) => {
      const isSnapshotsEndpoint = datasetId.includes("snapshots");
      let snapshotsParameters:
        | Partial<AnalyzeSitewideFrontendTinybirdRequestBodyType>
        | undefined;
      if (isSnapshotsEndpoint) {
        snapshotsParameters = {
          comparisonEndTs: snapshotsComparisonEndTime,
          comparisonStartTs: snapshotsComparisonStartTime,
          endTs: snapshotsEndTime,
          startTs: snapshotsStartTime,
        };
      }

      const payload = buildSitewideAnalyticsPayload({
        filters: {
          ...restOfDashboardFilters,
          landingPage: sitewideDashboardFilters.landingPage || null,
          sourceSitesOrChannels: sitewideDashboardFilters.channels,
          maxZScore: getMaxZScore(
            sitewideDashboardFilters.maxZScore,
            sitewideDashboardFilters.isZScoreEnabled,
            maxZScore
          ),
        },
        startTs: startTime
          ? DateTime.fromISO(startTime).setZone(timezone).toISO()
          : DateTime.now().setZone(timezone).toISO(),
        endTs: endTime
          ? DateTime.fromISO(endTime).setZone(timezone).toISO()
          : DateTime.now().setZone(timezone).toISO(),
        datasetId,
        interval: sitewideDashboardFilters.interval,
        allOrders: ordersMatchedToSession === SitewideOrdersScope.allOrders,
        ...snapshotsParameters,
      });

      return {
        ...payload,
        conversionFunnel,
        ...parameters,
      };
    },
  } as const;
};
